import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LINKS = [
  { to: "/approach", label: "Approach" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/case-library", label: "Cases" },
  { to: "/for-labs", label: "For Labs" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const ctaTo = user ? (user.role === "admin" ? "/admin" : "/portal") : "/login";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled || open ? "bg-ink/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="h-8 w-8 rounded-lg bg-neon flex items-center justify-center font-display font-black text-ink text-sm">OX</span>
          <span className="font-display font-800 font-bold text-lg tracking-tight text-white">
            OrthoPlani<span className="text-neon">X</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-neon ${isActive ? "text-neon" : "text-slate-300"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <Link to={ctaTo} className="text-sm font-medium text-slate-300 hover:text-neon transition-colors" data-testid="nav-portal-link">
              {user.role === "admin" ? "Admin Console" : "My Portal"}
            </Link>
          ) : (
            <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-neon transition-colors" data-testid="nav-login-link">
              Client Login
            </Link>
          )}
          <Link
            to={ctaTo}
            data-testid="nav-submit-case-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,229,255,0.4)]"
          >
            Submit a Case
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="text-base font-medium text-slate-200 hover:text-neon">
                  {l.label}
                </NavLink>
              ))}
              <Link to={ctaTo} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-neon px-5 py-3 text-sm font-bold text-ink" data-testid="nav-mobile-submit-btn">
                Submit a Case <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
